import { useState } from 'react';
import { Box, Input, Modal, Text } from '@nimbus-ds/components';
import { CancelAndConfirmButtons } from 'commons/components';
import { FiltersParamsType } from 'domains/Orders/Orders/ordersService';
import { trackingSavedSearchQuickSaveResult } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useSavedSearches } from '../../useSavedSearches';

const MAX_SAVED_SEARCH_NAME_LENGTH = 20;

interface CreateSavedSearchModalProps {
  open: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  filters: FiltersParamsType;
  confirmText?: string;
}

export function CreateSavedSearchModal({
  open,
  onDismiss,
  onConfirm,
  filters,
  confirmText,
}: Readonly<CreateSavedSearchModalProps>) {
  const t = useTranslationOrders('savedSearches.creationModal');
  const { createSavedSearch } = useSavedSearches();
  const [savedSearchName, setSavedSearchName] = useState('');

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value.trimStart();
    if (name.length <= MAX_SAVED_SEARCH_NAME_LENGTH) {
      setSavedSearchName(name);
    }
  };

  const handleDismissSavedSearch = () => {
    trackingSavedSearchQuickSaveResult('cancelled');
    setSavedSearchName('');
    onDismiss();
  };

  const handleConfirmSavedSearch = () => {
    trackingSavedSearchQuickSaveResult('confirmed');
    createSavedSearch(savedSearchName.trim(), filters);
    setSavedSearchName('');
    onConfirm?.();
  };

  return (
    <Modal open={open} onDismiss={handleDismissSavedSearch}>
      <Modal.Header>
        <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
          {t('title')}
        </Text>
      </Modal.Header>
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="2">
          <Text>{t('name')}</Text>
          <Input onChange={handleNameChange} value={savedSearchName} />
          <Text>
            {savedSearchName.length}/{MAX_SAVED_SEARCH_NAME_LENGTH}{' '}
            {t('maxChars')}
          </Text>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndConfirmButtons
          onCancel={handleDismissSavedSearch}
          onConfirm={handleConfirmSavedSearch}
          confirmText={confirmText}
          isConfirmDisabled={!savedSearchName.length}
        />
      </Modal.Footer>
    </Modal>
  );
}
